import { useState, useCallback, useRef, useEffect } from 'react';
import { ScaffoldSegment } from '../types';

// <purpose>Hook managing Klärfall slot-fill draft state for ONE suggestion. Exposes current
// slot values + confirmation flags, and mutators: setVal, clear, unlock, confirm.
// Derived gates: assemble, allConfirmed, openCount. confirm is optimistic-then-rollback
// (mirrors TicketManagerContext pattern) and advances focus to the next open slot.</purpose>
// <dependencies>ScaffoldSegment shape from types.ts; module-level draftStore (in-memory).</dependencies>
// <constraints>A draft belongs to a SUGGESTION, not a ticket: callers key it by the
// suggestion-attempt identity (ai_suggestions.id), so a reanalysis (new suggestion under
// the same ticket uuid) resets the draft to a clean slate. draftStore is intentionally NOT
// persisted to localStorage — slot values can contain PII (Vertragskonto, Zählerstand,
// names). Survives in-app nav + HMR; resets on hard reload. Mirrors the seenUuids module
// store in useReadState minus the storage write.
//
// arc243 Part A Task 7: draftStore is a same-session CACHE, not the source of truth — a
// confirmed fill must survive a hard reload. confirm/fillSystemSlots persist the current
// segment array (onPersist's optional 2nd arg) alongside the assembled text so the server
// can rewrite scaffold_json; on the next mount initFromSegments reconstructs the fill from
// what the server now serves (status: 'filled' → confirmed), never from draftStore alone.</constraints>

type DraftState = { values: Record<number, string>; confirmed: Record<number, boolean> };

const draftStore = new Map<string, DraftState>();
export function __resetDraftStore() { draftStore.clear(); } // test hook only

const isSlot = (s: ScaffoldSegment): s is Extract<ScaffoldSegment, { kind: 'slot' }> => s.kind === 'slot';

function initFromSegments(segments: ScaffoldSegment[]): DraftState {
  const values: Record<number, string> = {};
  const confirmed: Record<number, boolean> = {};
  segments.forEach((s, i) => {
    if (isSlot(s)) {
      values[i] = s.value ?? '';
      // filled (mail-sourced, trusted) = confirmed; unconfirmed/placeholder need rep action
      confirmed[i] = s.status === 'filled';
    }
  });
  return { values, confirmed };
}

function assembleText(
  segments: ScaffoldSegment[],
  values: Record<number, string>,
  confirmed: Record<number, boolean>,
): string {
  return segments.map((s, i) => {
    if (!isSlot(s)) return s.value;
    return confirmed[i] ? (values[i] ?? '') : `[${s.label}]`;
  }).join('');
}

// The FULL segment array reflecting the current draft, for the server to persist into
// scaffold_json. Text segments pass through unchanged. Slot segments carry the current typed
// value; a confirmed slot's status becomes 'filled' (so a reload's initFromSegments reads it
// back as confirmed) — an unlocked slot (status was 'filled', now unconfirmed) demotes back to
// 'unconfirmed' rather than staying 'filled' out from under its own confirmed flag.
function currentSegments(
  segments: ScaffoldSegment[],
  values: Record<number, string>,
  confirmed: Record<number, boolean>,
): ScaffoldSegment[] {
  return segments.map((s, i) => {
    if (!isSlot(s)) return s;
    if (confirmed[i]) return { ...s, status: 'filled', value: values[i] ?? '' };
    return { ...s, status: s.status === 'filled' ? 'unconfirmed' : s.status, value: values[i] ?? s.value };
  });
}

export interface KlaerfallDraft {
  values: Record<number, string>;
  isConfirmed: (i: number) => boolean;
  setVal: (i: number, v: string) => void;
  clear: (i: number) => void;
  unlock: (i: number) => void;
  assemble: () => string;
  allConfirmed: boolean;
  openCount: number;
  confirm: (i: number) => Promise<void>;
  fillSystemSlots: (fills: { index: number; value: string }[]) => Promise<void>;
  registerInput: (i: number, el: HTMLInputElement | null) => void;
}

export function useKlaerfallDraft(
  draftKey: string,
  segments: ScaffoldSegment[],
  // scaffoldSegments is the 2nd (optional) arg: omitted when there is no scaffold to persist
  // into (segments === []) so the wire PATCH stays edited_text-only, byte-identical to before
  // Task 7. Present whenever there IS a scaffold, so the server can rewrite scaffold_json and a
  // reload's initFromSegments sees the fill.
  onPersist: (text: string, scaffoldSegments?: ScaffoldSegment[]) => Promise<boolean>,
): KlaerfallDraft {
  const [state, setState] = useState<DraftState>(() => draftStore.get(draftKey) ?? initFromSegments(segments));

  // Sync re-init when the draft key changes (selected ticket OR a new suggestion attempt
  // under the same ticket, i.e. a reanalysis); read prior progress from store if present.
  const prevKey = useRef(draftKey);
  if (prevKey.current !== draftKey) {
    prevKey.current = draftKey;
    setState(draftStore.get(draftKey) ?? initFromSegments(segments));
  }

  // Write back to module store on every state change so remounts preserve progress.
  useEffect(() => { draftStore.set(draftKey, state); }, [draftKey, state]);

  const setVal = useCallback((i: number, v: string) => {
    setState(s => ({ ...s, values: { ...s.values, [i]: v } }));
  }, []);

  const isConfirmed = useCallback((i: number) => !!state.confirmed[i], [state.confirmed]);

  const clear = useCallback((i: number) => {
    setState(s => ({ ...s, values: { ...s.values, [i]: '' } }));
  }, []);

  const unlock = useCallback((i: number) => {
    setState(s => ({ ...s, confirmed: { ...s.confirmed, [i]: false } }));
  }, []);

  const assemble = useCallback(
    () => assembleText(segments, state.values, state.confirmed),
    [segments, state],
  );

  const allConfirmed = segments.every((s, i) => !isSlot(s) || state.confirmed[i]);
  const openCount = segments.reduce((n, s, i) => (isSlot(s) && !state.confirmed[i] ? n + 1 : n), 0);

  // ─── confirm: optimistic → persist → rollback on failure ──────────────────
  // Mirrors TicketManagerContext optimistic shape: apply optimistically, await,
  // rollback if !ok. Focus advances to the next open slot immediately (pre-await)
  // so the UX feels snappy — rollback will re-open the slot visually if persist fails.

  const inputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  const registerInput = useCallback((i: number, el: HTMLInputElement | null) => {
    inputRefs.current[i] = el;
  }, []);

  const focusNextOpen = useCallback(
    (fromIndex: number, confirmedNow: Record<number, boolean>) => {
      const openAfter = segments
        .map((s, i) => (isSlot(s) && !confirmedNow[i] && i !== fromIndex ? i : -1))
        .filter(i => i >= 0);
      const next = openAfter.find(i => i > fromIndex) ?? openAfter[0];
      if (next != null) inputRefs.current[next]?.focus();
    },
    [segments],
  );

  const confirm = useCallback(
    async (i: number) => {
      const nextConfirmed = { ...state.confirmed, [i]: true };
      setState(s => ({ ...s, confirmed: { ...s.confirmed, [i]: true } }));
      focusNextOpen(i, nextConfirmed);
      const text = assembleText(segments, state.values, nextConfirmed);
      const nextSegments = segments.length > 0 ? currentSegments(segments, state.values, nextConfirmed) : undefined;
      const ok = await onPersist(text, nextSegments);
      if (!ok) setState(s => ({ ...s, confirmed: { ...s.confirmed, [i]: false } }));
    },
    [state, segments, onPersist, focusNextOpen],
  );

  // ─── fillSystemSlots: bulk fill+confirm the given slots in ONE state update + ONE persist ──
  // Drives the "Simulierte Systemdaten" demo action. A per-slot setVal/confirm loop would persist
  // stale text (setState is async — confirm reads state.values from its render's closure), so the
  // fill must be atomic. Optimistic → persist merged text → roll back only the confirmations on
  // failure (mirrors confirm()). Values are index-keyed to match the segment-index draft model.
  const fillSystemSlots = useCallback(
    async (fills: { index: number; value: string }[]) => {
      if (fills.length === 0) return;
      const nextValues = { ...state.values };
      const nextConfirmed = { ...state.confirmed };
      fills.forEach(({ index, value }) => { nextValues[index] = value; nextConfirmed[index] = true; });
      setState(s => ({ values: nextValues, confirmed: nextConfirmed }));
      const text = assembleText(segments, nextValues, nextConfirmed);
      const nextSegments = segments.length > 0 ? currentSegments(segments, nextValues, nextConfirmed) : undefined;
      const ok = await onPersist(text, nextSegments);
      if (!ok) setState(s => ({
        ...s,
        confirmed: { ...s.confirmed, ...Object.fromEntries(fills.map(f => [f.index, false])) },
      }));
    },
    [state, segments, onPersist],
  );

  return { values: state.values, isConfirmed, setVal, clear, unlock, assemble, allConfirmed, openCount, confirm, fillSystemSlots, registerInput };
}
